import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useArchivedAttachmentViewer } from './ArchivedAttachmentViewer';
import BEM from 'common/bem';
import useInfiniteScrollSentinel from 'common/hooks/useInfiniteScrollSentinel';

const classes = BEM.with('ConversationThreadPanel');
const scrimClasses = BEM.with('ConversationThreadPanelScrim');
const EMPTY_SELECTION: Set<string> = new Set();
export const CONVERSATION_PAGE_SIZE = 25;
// When the panel opens focused on a message that isn't in the first page, keep paging until it
// shows up — bounded so a stale/missing id can't page through an enormous thread.
export const FOCUS_CATCH_UP_MAX_PAGES = 40;

type ConversationMessage = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

type ConversationMetadata = {
  conversationId: string;
  channelId: string | null;
  messageCount: number;
};

type ConversationClient = {
  archiving: {
    getConversation: (
      organizationId: string,
      conversationId: string,
      options?: { limit?: number; nextToken?: string }
    ) => Promise<{ data: { messages: ConversationMessage[]; nextToken?: string | null } }>;
    getConversationMetadata: (
      organizationId: string,
      conversationId: string
    ) => Promise<{ data: ConversationMetadata }>;
    getMessageAttachment: (
      organizationId: string,
      messageId: string
    ) => Promise<{ blob: Blob; url: string }>;
  };
};

type ConversationThreadPanelProps = {
  client: ConversationClient | null;
  currentOrganizationId: string;
  conversationId: string;
  focusedMessageId?: string;
  onClose: () => void;
  selectedMessageIds?: Set<string>;
  onAddToExport?: (messages: ConversationMessage[]) => void;
  /** Every message ID in the conversation, when known from a completed full-thread add. */
  fullThreadMessageIds?: string[];
};

/**
 * "1:1 Direct" vs "Group" is a placeholder derived from channelId's presence — per xmpp's
 * conversation model, channelId is null for 1:1 messages and populated for group/distribution-list
 * ones. This isn't a real channel-type field (none is confirmed on the Snowflake schema yet); it's
 * the best signal already proven to exist until data eng confirms a dedicated column. Sourced from
 * the metadata endpoint rather than the loaded messages array, so it stays correct once the
 * messages endpoint paginates — see design doc 0002 §5.12.
 */
function deriveConversationTypeLabel(metadata: ConversationMetadata): string {
  return metadata.channelId ? 'Group' : '1:1 Direct';
}

export function ConversationThreadPanel({
  client,
  currentOrganizationId,
  conversationId,
  focusedMessageId,
  onClose,
  selectedMessageIds = EMPTY_SELECTION,
  onAddToExport = () => {},
  fullThreadMessageIds,
}: ConversationThreadPanelProps) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // bumped whenever the conversation changes so a load-more response for the previous
  // conversation can't append into the new one.
  const loadSeq = useRef(0);
  const [metadata, setMetadata] = useState<ConversationMetadata | null>(null);
  const focusedMessageRef = useRef<HTMLLIElement | null>(null);
  const hasScrolledToFocusedMessage = useRef(false);
  const attachmentViewer = useArchivedAttachmentViewer(client, currentOrganizationId);

  useEffect(() => {
    hasScrolledToFocusedMessage.current = false;

    if (!client) return;

    let isCancelled = false;
    loadSeq.current += 1;
    setIsLoading(true);
    setError(null);
    setMessages([]);
    setNextToken(null);
    setIsLoadingMore(false);
    setLoadMoreError(null);

    const activeClient = client;
    async function loadInitialPages() {
      try {
        let response = await activeClient.archiving.getConversation(
          currentOrganizationId,
          conversationId,
          { limit: CONVERSATION_PAGE_SIZE }
        );
        if (isCancelled) return;
        let loaded = response?.data?.messages || [];
        let token = response?.data?.nextToken || null;
        let pages = 1;
        // Catch up to the focused message so the focus effect below can scroll to it. Pages keep
        // the regular page size so the cursor left behind continues seamlessly for infinite
        // scroll; the loading state stays up until the walk finishes.
        while (
          focusedMessageId &&
          token &&
          pages < FOCUS_CATCH_UP_MAX_PAGES &&
          !loaded.some((message) => message.messageId === focusedMessageId)
        ) {
          response = await activeClient.archiving.getConversation(
            currentOrganizationId,
            conversationId,
            { limit: CONVERSATION_PAGE_SIZE, nextToken: token }
          );
          if (isCancelled) return;
          loaded = [...loaded, ...(response?.data?.messages || [])];
          token = response?.data?.nextToken || null;
          pages += 1;
        }
        setMessages(loaded);
        setNextToken(token);
      } catch (err) {
        if (isCancelled) return;
        setError('Something went wrong loading this conversation. Please try again.');
        setMessages([]);
        setNextToken(null);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }
    loadInitialPages();

    return () => {
      isCancelled = true;
    };
  }, [client, currentOrganizationId, conversationId, focusedMessageId]);

  useEffect(() => {
    if (!client) return;

    let isCancelled = false;
    setMetadata(null);

    client.archiving
      .getConversationMetadata(currentOrganizationId, conversationId)
      .then((response) => {
        if (isCancelled) return;
        setMetadata(response?.data || null);
      })
      .catch(() => {
        if (isCancelled) return;
        setMetadata(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [client, currentOrganizationId, conversationId]);

  useEffect(() => {
    if (hasScrolledToFocusedMessage.current) return;
    if (!focusedMessageRef.current) return;

    focusedMessageRef.current.scrollIntoView({ block: 'center' });
    hasScrolledToFocusedMessage.current = true;
    // isLoading too: the list (and so the focused row's ref) only mounts once loading clears,
    // which can be a separate render from the one that set messages.
  }, [messages, isLoading]);

  function loadMore() {
    if (!client || !nextToken || isLoadingMore) return;
    const seq = loadSeq.current;
    setIsLoadingMore(true);
    setLoadMoreError(null);

    client.archiving
      .getConversation(currentOrganizationId, conversationId, {
        limit: CONVERSATION_PAGE_SIZE,
        nextToken,
      })
      .then((response) => {
        if (seq !== loadSeq.current) return;
        setMessages((prev) => [...prev, ...(response?.data?.messages || [])]);
        setNextToken(response?.data?.nextToken || null);
      })
      .catch(() => {
        if (seq !== loadSeq.current) return;
        setLoadMoreError('Something went wrong loading more messages.');
      })
      .finally(() => {
        if (seq !== loadSeq.current) return;
        setIsLoadingMore(false);
      });
  }

  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage: !!nextToken && !isLoading && !error && !loadMoreError,
    isFetchingNextPage: isLoadingMore,
    onLoadMore: loadMore,
  });

  const threadMessagesWithId = messages.filter(
    (message): message is ConversationMessage & { messageId: string } => !!message.messageId
  );
  // While nextToken is set there are unloaded messages, so every loaded message being selected
  // isn't enough on its own — unless a full-thread add told us every ID in the conversation and
  // those are all still selected (a later deselect re-enables the button).
  const isThreadFullyAdded =
    (!nextToken &&
      threadMessagesWithId.length > 0 &&
      threadMessagesWithId.every((message) => selectedMessageIds.has(message.messageId))) ||
    (!!fullThreadMessageIds &&
      fullThreadMessageIds.length > 0 &&
      fullThreadMessageIds.every((id) => selectedMessageIds.has(id)));

  return (
    <>
      <div
        aria-hidden="true"
        className={scrimClasses()}
        data-test-id="conversation-thread-scrim"
        onClick={onClose}
      />
      <div className={classes()} data-test-id="conversation-thread-panel" role="dialog">
        <div className={classes('header')}>
          <button
            aria-label="Close conversation"
            className={classes('close-button')}
            data-test-id="conversation-thread-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
          <div className={classes('header-text')}>
            <div className={classes('header-title')}>
              {metadata ? deriveConversationTypeLabel(metadata) : 'Conversation'}
            </div>
            <div className={classes('header-subtitle')}>
              {metadata &&
                `${metadata.messageCount} message${metadata.messageCount === 1 ? '' : 's'}`}
            </div>
          </div>
          <button
            className={classes('export-button')}
            data-test-id="conversation-add-to-export"
            disabled={threadMessagesWithId.length === 0 || isThreadFullyAdded}
            onClick={() => onAddToExport(threadMessagesWithId)}
            type="button"
          >
            {isThreadFullyAdded ? 'Added to Export' : 'Add to Export'}
          </button>
        </div>

        <div className={classes('main')}>
          <div className={classes('body')} data-test-id="conversation-thread-body">
            {isLoading && <div className={classes('loading-state')}>Loading conversation…</div>}
            {error && <div className={classes('error')}>{error}</div>}
            {!isLoading && !error && messages.length === 0 && (
              <div className={classes('empty-state')}>No messages found in this conversation.</div>
            )}
            {!isLoading && !error && messages.length > 0 && (
              <ul className={classes('message-list')}>
                {messages.map((message, index) => (
                  <li
                    className={classes('message', {
                      focused: message.messageId === focusedMessageId,
                    })}
                    key={message.messageId || index}
                    ref={message.messageId === focusedMessageId ? focusedMessageRef : undefined}
                  >
                    <div className={classes('message-meta')}>
                      <span className={classes('message-sender')}>{message.senderDisplayName}</span>
                      <span className={classes('message-date')}>
                        {message.sentAt && moment(message.sentAt).format('MMM D, YYYY h:mm A')}
                      </span>
                    </div>
                    {message.body && <div className={classes('message-body')}>{message.body}</div>}
                    {message.hasAttachment && (
                      <button
                        className={classes('message-attachment')}
                        data-test-id={`conversation-thread-view-attachment-${
                          message.messageId || index
                        }`}
                        disabled={
                          !message.messageId || attachmentViewer.isLoading === message.messageId
                        }
                        onClick={() => attachmentViewer.view(message.messageId)}
                        type="button"
                      >
                        {attachmentViewer.isLoading === message.messageId
                          ? '📎 Loading…'
                          : '📎 View Attachment'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!isLoading && !error && nextToken && (
              <div
                className={classes('load-more-sentinel')}
                data-test-id="conversation-thread-sentinel"
                ref={sentinelRef}
              />
            )}
            {isLoadingMore && (
              <div
                className={classes('loading-state')}
                data-test-id="conversation-thread-loading-more"
              >
                Loading more messages…
              </div>
            )}
            {loadMoreError && (
              <div className={classes('error')}>
                {loadMoreError}{' '}
                <button
                  className={classes('retry-button')}
                  data-test-id="conversation-thread-load-more-retry"
                  onClick={loadMore}
                  type="button"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          <div className={classes('details')}>
            <div className={classes('details-title')}>Details</div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Conversation ID</span>
              <span className={classes('details-value')}>{conversationId}</span>
            </div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Type</span>
              <span className={classes('details-value')}>
                {metadata ? deriveConversationTypeLabel(metadata) : '—'}
              </span>
            </div>
            <div className={classes('details-row')}>
              <span className={classes('details-label')}>Thread size</span>
              <span className={classes('details-value')}>
                {metadata ? `${metadata.messageCount} messages` : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {attachmentViewer.modal}
    </>
  );
}

export default ConversationThreadPanel;
